'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { usePathname } from '@/src/i18n/navigation';
import { useCurrentUser, useUserListings } from '@/src/lib/hooks/query';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { ListingCard } from '@/src/components/ListingCard';
import { SAVE_CLICKSTREAM_CONTEXTS } from '@/src/components/features/listing-actions/listingActionUtils';
import { CAROUSEL_CARD_IMAGE_SIZES, ListingCarousel } from '@/src/components/ListingCarousel';
import { carouselItem, container, loadingCard, loadingRow } from './recentlyViewedModule.styles';
import { selectRecentlyViewedListings } from './recentlyViewedModule.utils';

type Props = {
  listing: PropertyDetailListing;
};

export const RecentlyViewedModule = ({ listing }: Props) => {
  const t = useTranslations('hdp');
  const pathname = usePathname();
  const { data: currentUser } = useCurrentUser();
  const { data, isLoading, error } = useUserListings('viewed', {
    enabled: currentUser?.loggedIn === true,
  });
  const currentListingUri = pathname || listing.uriMalone || listing.uriBuilding || '';

  const filteredListings = useMemo(
    () => selectRecentlyViewedListings(data?.listings, currentListingUri),
    [currentListingUri, data?.listings],
  );

  if (isLoading) {
    return (
      <div className={loadingRow} role="status" aria-label={t('loadingRecentlyViewed')} aria-busy="true">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className={loadingCard} />
        ))}
      </div>
    );
  }

  if (error || filteredListings.length === 0) {
    return null;
  }

  return (
    <div className={container}>
      <ListingCarousel aria-label={t('recentlyViewedHomes')}>
        {filteredListings.map((listing) => (
          <div
            key={listing.aliasEncoded ?? (listing as { uriV2?: string }).uriV2 ?? listing.uriMalone}
            className={carouselItem}
          >
            <ListingCard
              clickstreamTriggerLocation={'recently_viewed_listings_module'}
              saveClickstreamContext={SAVE_CLICKSTREAM_CONTEXTS.recentlyViewed}
              listing={listing}
              sizes={CAROUSEL_CARD_IMAGE_SIZES}
            />
          </div>
        ))}
      </ListingCarousel>
    </div>
  );
};
